/**
 * הקמת לקוח — the four provisioning steps. OA-0001 §3.9.
 *
 *   > *"A SaaS product cannot be considered operational if it cannot admit its first customer …
 *   > the first administrator must not require direct database editing. Do not solve initial
 *   > provisioning through an undocumented SQL insert."*
 *
 * The four steps, and they are the four routes in `apps/api/src/routes/provisioning.ts` — one form
 * per route, in the order the server requires, because step 2 cannot be posted until step 1 has
 * returned the tenant it belongs to:
 *
 *   1. הקמת הלקוח                platform.tenant.create
 *   2. פתיחת מנוי                platform.subscription.create
 *   3. מודולים ומכסות שנרכשו     platform.subscription.sku.add
 *   4. הזמנת מנהל הארגון הראשון   platform.tenant.activate
 *
 * ── WHY THIS IS A SCREEN AND NOT A MODAL ────────────────────────────────────────────────────────
 *
 * 09 §2.1 M3: a modal is ONE DECISION, not a workflow. This is four writes against four routes with
 * a value carried between them; it has an address, and losing the page loses real server state that
 * has to be findable again — which is M5's test failed outright.
 *
 * ── AND WHY EACH STEP POSTS WHEN IT IS COMPLETED ────────────────────────────────────────────────
 *
 * Not at the end, in a batch. A write is NEVER retried by `api/client.ts` — the client cannot know
 * whether the server committed before the connection died — so a four-write batch that failed
 * halfway would leave an operator unable to say which half happened. One step, one write, one
 * visible outcome. Steps 5-11 belong to the CUSTOMER and appear nowhere on this screen: GL-POINT
 * creates the account and the commercial terms, and then stops (OA-0001 §3.2).
 *
 * ── WHAT HAPPENS WITH NO API ────────────────────────────────────────────────────────────────────
 *
 * The forms are wired to `api/client.ts`, which is the only place this app talks to the server. With
 * no server they fail, and the screen SAYS the request did not reach anything. It does not advance,
 * and it does not invent a tenant uid — a wizard that congratulated an operator on a customer that
 * does not exist is the exact defect the honesty rule of this milestone is written against.
 */

import { useCallback, useState } from 'react';
import type { FormEvent } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { DenialNotice } from '../components/DenialNotice';
import { ServerRead } from '../components/ServerRead';
import { ErrorState, StateBlock } from '../components/StateBlock';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { ApiError, ConflictError, DeniedError } from '../api/client';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import type { DenyBody, MailDeliveryReason } from '../api/contract';
import { MAIL_DELIVERY_REASONS } from '../api/contract';
import { deliveryTextFor } from '../api/deliveryText';
import { routes } from '../routes';

const TITLE_HE = 'הקמת לקוח חדש';

/** The four steps, named as the operator experiences them. Each maps to exactly one route. */
const STEPS_HE: readonly string[] = [
  'הקמת הלקוח',
  'פתיחת מנוי',
  'מודולים ומכסות שנרכשו',
  'הזמנת מנהל הארגון הראשון',
];

/**
 * 02 §5.2 — the customer kinds. Reporting only: `kind` is on the entitlement resolver's FORBIDDEN
 * INPUT list (CF-66), so it records what sort of customer this is and can never reach a decision.
 */
const TENANT_KINDS: readonly { readonly code: string; readonly labelHe: string }[] = [
  { code: 'cpa_firm', labelHe: 'ארגון רואי חשבון' },
  { code: 'direct_business', labelHe: 'עסק ישיר' },
  { code: 'internal', labelHe: 'פנימי' },
];

const PERIOD_KINDS: readonly { readonly code: string; readonly labelHe: string }[] = [
  { code: 'month', labelHe: 'חודשי' },
  { code: 'year', labelHe: 'שנתי' },
];

/** Progress is a value the server handed back, never a counter the screen incremented for itself. */
type Progress =
  /**
   * RESUMING, AND THE SERVER HAS NOT SAID WHERE YET.
   *
   * Only reachable at `/platform/tenants/{uid}/provisioning`. The step a half-provisioned customer
   * resumes at is a FACT ABOUT THE ESTATE -- has it a live subscription, has that subscription any
   * modules -- so it is read, never guessed from what the operator last did in this tab.
   */
  | { readonly step: 'resolving' }
  | { readonly step: 1 }
  | { readonly step: 2; readonly tenantUid: string }
  | { readonly step: 3; readonly tenantUid: string }
  | { readonly step: 4; readonly tenantUid: string }
  /*
   * DONE, AND WHAT ACTUALLY HAPPENED TO THE MESSAGE. **Owner, 2026-10-23 §8.**
   *
   * The last step carried only the invitation's id, so the screen could say "ההזמנה נשלחה" and
   * nothing else — while the server was answering **202** with `delivery: 'mail_suppressed'`,
   * meaning the invitation is real and NOBODY WAS SENT ANYTHING. An operator reading "נשלחה"
   * waits for an email that was never posted, which is exactly the reading §8 names.
   */
  | DoneProgress;

/** The last step, named so the three delivery mappers below take the whole thing and not a field. */
interface DoneProgress {
  readonly step: 'done';
  readonly tenantUid: string;
  readonly invitationUid: string;
  /** Absent ⇒ 201, the message was accepted by a mail path. */
  readonly delivery?: string;
  /** `file` ⇒ the invitation was written as an artifact and no message was posted. */
  readonly deliveryChannel?: string;
  readonly expiresAt?: string | null;
  readonly activated?: boolean;
}

type Failure =
  | { readonly kind: 'denied'; readonly body: DenyBody }
  /**
   * W3(h). A 409 whose reason the SERVER already knows, kept apart from the generic error.
   *
   * The Owner's direction, verbatim: *"Do not collapse materially different states into generic
   * 403/409/500 when the server already knows the actionable reason."* Until the store discriminated
   * them, an empty catalogue arrived here as `subscription_exists` and this screen printed "a
   * technical fault occurred" over it — twice wrong, in two different places.
   */
  | { readonly kind: 'conflict'; readonly reason: string; readonly requestUid: string }
  | { readonly kind: 'error'; readonly requestUid: string }
  | { readonly kind: 'unreachable' };

/**
 * THE WRITTEN NOT-CONFIGURED STATE FOR AN EMPTY CATALOGUE. Master §98 (M:725): a truthful
 * `NOT CONFIGURED` state, not a generic conflict and not a fixture.
 *
 * It REPORTS the state and offers no button.
 *
 * ── THAT CLAUSE WAS "NOTHING TRUTHFUL TO OFFER", AND X3 CHANGED THE FACT BEHIND IT ──────────────
 *
 * It used to read that no catalogue screen and no management route existed in this build, so pointing
 * at one would be the placeholder M:704 forbids. **Both now exist**: `/platform/catalogue` and five
 * `platform.catalogue.*` routes shipped with X3 (R-2). The sentence is corrected here rather than left
 * standing, because a comment asserting an absence that has been filled is the artifact a reviewer
 * trusts.
 *
 * WHAT IS STILL NOT DONE, AND DELIBERATELY: no link is added from this state to that address. A next
 * step is product copy and a navigation decision, and neither is inside X3's change surface — the
 * Architect's X3 section does not list this file. Recorded as a RECOMMENDATION in the X3
 * implementation report instead of taken here.
 *
 * ── THESE SENTENCES ARE NEW PRODUCT COPY, AND THE RECORD NOW SAYS SO (S4W F2) ───────────────────
 *
 * `StateBlock` takes its words per use, so §2 W3.2 fix 5 — *"render `catalogue_empty` as the defined
 * NOT CONFIGURED state with the next valid action, using `StateBlock`"* — cannot be satisfied without
 * writing sentences. Seven of them stand, disclosed in `IMPLEMENTATION_GLPOINT-S4W-W4-W5_cycle2.md`
 * and awaiting the Product Lead's confirmation of the wording. Each states only what just happened.
 *
 * ONE WAS REMOVED, and the reason belongs here. The `catalogue_empty` arm carried a next step reading
 * *"catalogue configuration is not performed on this screen; approach whoever manages the plan
 * catalogue."* The Master does not merely leave that undefined — it CONTRADICTS it: §98 (M:725) lists
 * *"Plans / SKUs / Module Pricing"* among the REQUIRED Platform navigation items, and §99 (M:728)
 * requires that *"the Platform Owner must be able to operate the commercial model even with zero
 * customers"*, listing *"plan/module/SKU pricing"* as a required control-plane capability. The
 * catalogue is the Platform Owner's own to operate, and the Platform Owner is who reads this screen.
 * So the sentence sent the one person the Master makes responsible to go and find somebody else.
 *
 * No replacement wording was invented. The next step is simply absent — the same shape the
 * `subscription_exists` arm below already has — and the title and body still state the truth: there is
 * no active plan, and nothing was saved. That the capability is missing from this build is held
 * decision B, which is reported, not narrated at the user.
 */
function conflictNotice(reason: string, requestUid: string): JSX.Element | null {
  if (reason === 'catalogue_empty') {
    return (
      <StateBlock
        titleHe="אין תכנית מנוי פעילה בקטלוג"
        bodyHe="פתיחת מנוי מחייבת תכנית פעילה, ובקטלוג אין אף תכנית פעילה. המנוי לא נפתח ושום דבר לא נשמר."
        tone="unavailable"
      />
    );
  }
  if (reason === 'plan_unknown') {
    return (
      <StateBlock
        titleHe="קוד התכנית אינו תכנית פעילה"
        bodyHe="הקוד שהוזן אינו קוד של תכנית פעילה בקטלוג. המנוי לא נפתח ושום דבר לא נשמר."
        tone="unavailable"
        nextStep={<p>אפשר לתקן את קוד התכנית ולשלוח את השלב שוב.</p>}
      />
    );
  }
  if (reason === 'subscription_exists') {
    return (
      <StateBlock
        titleHe="ללקוח הזה כבר יש מנוי פעיל"
        bodyHe="קיים מנוי חי אחד ללקוח, ולכן לא נפתח מנוי נוסף."
        tone="unavailable"
      />
    );
  }
  // A conflict this screen has not been taught about. The written generic, with the reference code —
  // honest rather than silent, and never a sentence invented for a reason nobody mapped.
  return <ErrorState requestUid={requestUid} />;
}

/**
 * ── THE DELIVERY OUTCOME, IN WORDS. **Owner, 2026-10-23 §8.** ──────────────────────────────────
 *
 * Three answers, and the wizard used to give one. `deliveryTextFor` already holds the Hebrew for
 * the two mail outcomes — written for the office's own invitation screen — so this reuses it
 * rather than writing a second, drifting copy of the same three sentences.
 *
 * `tenant_not_activated` is the third thing the route can send, and it is NOT a mail outcome: the
 * message went out and the customer did not move to `active`. Rendering it through the mail
 * vocabulary would tell an operator to chase a message that was delivered.
 *
 * NO ENVIRONMENT IS CONSULTED. Whether this estate has a mail path is a fact about the SERVER, and
 * the server states it per call. Deciding it from the environment ribbon would be the same mistake
 * as reading a release's identity out of a configuration file: a claim about the estate inferred
 * from something beside it.
 */
function isMailOutcome(delivery: string): delivery is MailDeliveryReason {
  return (MAIL_DELIVERY_REASONS as readonly string[]).includes(delivery);
}

/**
 * THE CHANNEL OUTRANKS THE OUTCOME, AND THAT IS NOT A PREFERENCE. **Owner, 2026-10-23 §8.**
 *
 * `deliveryChannel === 'file'` means the port is a DIRECTORY. The write succeeded, so the outcome
 * is `sent` and the route answers 201 with no `delivery` at all — which is exactly the response a
 * posted message produces. Reading the outcome first would therefore print "ההזמנה נשלחה" on the
 * estate where nothing was posted, which is the sentence §8 is about.
 */
function writtenToAFile(channel: string | undefined): boolean {
  return channel === 'file';
}

function deliveryHeading(progress: DoneProgress): string {
  if (writtenToAFile(progress.deliveryChannel)) return 'ההזמנה נרשמה — לא נשלח דואר';
  if (progress.delivery === undefined) return 'הלקוח הוקם וההזמנה נשלחה';
  if (isMailOutcome(progress.delivery)) return deliveryTextFor(progress.delivery).titleHe;
  return 'ההזמנה נרשמה — הלקוח טרם הופעל';
}

const HANDOVER_HE =
  'מכאן ואילך המשך ההקמה שייך ללקוח: מנהל הארגון מצטרף, מזמין משתמשים, מקים חברות ומעניק גישה — ללא מעורבות של הפלטפורמה.';

function deliveryBody(progress: DoneProgress): string {
  if (writtenToAFile(progress.deliveryChannel)) {
    return (
      'ההזמנה נוצרה והקישור שבה תקף. בסביבה הזאת אין נתיב דואר יוצא: ההודעה נכתבה כקובץ בתיקיית ' +
      'ההודעות של השרת, ולא נשלח דואר לאף כתובת. אין להמתין להודעה בתיבת הדואר של המוזמן. ' +
      HANDOVER_HE
    );
  }
  if (progress.delivery === undefined) return HANDOVER_HE;
  if (isMailOutcome(progress.delivery)) {
    return `${deliveryTextFor(progress.delivery).explanationHe} ${HANDOVER_HE}`;
  }
  return (
    'ההזמנה קיימת והקישור שבה תקף, אך הלקוח לא עבר למצב פעיל בקריאה הזאת. ' +
    'ההזמנה אינה מבטלת את זה ואינה מושפעת מזה. ' +
    HANDOVER_HE
  );
}

function deliveryNextStep(progress: DoneProgress): string {
  if (writtenToAFile(progress.deliveryChannel)) {
    /*
     * NO LINK AND NO TOKEN ON THIS SCREEN. The artifact holds the redemption link; this console
     * never shows one, and the operator who may read the outbox is the one with access to the
     * host. Printing it here would put a credential on a screen, which 04 §2.2 forbids outright.
     */
    return (
      'יש למסור למוזמן בדרך אחרת שההזמנה ממתינה לו. הקישור עצמו נמצא בקובץ ההודעה על השרת ואינו ' +
      'מוצג במסך הזה. אין ליצור הזמנה נוספת לאותו אדם — היא תתפוס מושב שני.'
    );
  }
  if (progress.delivery === undefined) {
    return 'מנהל הארגון יקבל את ההזמנה בדואר ויקבע סיסמה בעצמו.';
  }
  if (isMailOutcome(progress.delivery)) return deliveryTextFor(progress.delivery).nextStepHe;
  return 'יש לבדוק את מצב הלקוח בכרטיס שלו. ההזמנה עצמה אינה דורשת פעולה נוספת.';
}

function stepNumber(progress: Progress): number {
  // `0` while resolving: no step is current yet, so the rail shows all four as pending rather than
  // lighting one the server has not confirmed.
  if (progress.step === 'resolving') return 0;
  return progress.step === 'done' ? STEPS_HE.length + 1 : progress.step;
}

export interface PlatformTenantProvisioningScreenProps {
  /**
   * An EXISTING customer to finish. Absent, this is the four-step creation of a new one.
   *
   * It arrives from the ADDRESS (`/platform/tenants/{uid}/provisioning`) and never from state, so
   * the page survives a reload, a bookmark and a link from the subscription screen -- which is the
   * whole of what was missing when `rafi-test-20261018` committed its tenant row and nothing else.
   */
  readonly resumeTenantUid?: string;
}

export function PlatformTenantProvisioningScreen({
  resumeTenantUid,
}: PlatformTenantProvisioningScreenProps = {}): JSX.Element {
  const ctx = useWorkContext();

  /*
   * TWO READS, AND ONLY WHEN RESUMING.
   *
   * `null` rather than a skipped hook: hooks are unconditional, and a read that resolves to `null`
   * is how this screen stays ONE component instead of two that drift. Creating a new customer makes
   * no request at all.
   */
  const readTenants = useCallback(
    async () =>
      resumeTenantUid === undefined ? null : await callOperation('platform.tenant.read', {}),
    [resumeTenantUid],
  );
  const readSubscription = useCallback(
    async () =>
      resumeTenantUid === undefined
        ? null
        : await callOperation('platform.subscription.read', {
            params: { tenant_uid: resumeTenantUid },
          }),
    [resumeTenantUid],
  );
  const tenantsRead = useServerData(readTenants);
  const subscriptionRead = useServerData(readSubscription);

  const [progress, setProgress] = useState<Progress | null>(
    resumeTenantUid === undefined ? { step: 1 } : null,
  );
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);

  const [legalName, setLegalName] = useState('');
  const [slug, setSlug] = useState('');
  const [kind, setKind] = useState('cpa_firm');
  const [planCode, setPlanCode] = useState('');
  const [periodKind, setPeriodKind] = useState('year');
  const [skuCode, setSkuCode] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');

  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }

  /**
   * One write, one outcome. `post` never retries — see the binding retry policy in `api/client.ts` —
   * so a transport failure here is reported and the step is NOT advanced.
   */
  async function submitStep(run: () => Promise<Progress>): Promise<void> {
    setPending(true);
    setFailure(null);
    try {
      setProgress(await run());
    } catch (err: unknown) {
      if (err instanceof DeniedError) {
        setFailure({ kind: 'denied', body: err.body });
      } else if (err instanceof ApiError && err.status === 0) {
        setFailure({ kind: 'unreachable' });
      } else if (err instanceof ConflictError) {
        // W3(h). BEFORE the generic `ApiError` arm — a `ConflictError` IS an `ApiError`, so the
        // order is the whole of the classification.
        setFailure({ kind: 'conflict', reason: err.body.error, requestUid: err.requestUid });
      } else if (err instanceof ApiError) {
        setFailure({ kind: 'error', requestUid: err.requestUid });
      } else {
        setFailure({ kind: 'unreachable' });
      }
    } finally {
      setPending(false);
    }
  }

  function onCreateTenant(e: FormEvent): void {
    e.preventDefault();
    void submitStep(async () => {
      const created = await callOperation('platform.tenant.create', {
        body: { slug, legalName, kind },
      });
      return { step: 2, tenantUid: created.tenantUid };
    });
  }

  function onCreateSubscription(e: FormEvent, tenantUid: string): void {
    e.preventDefault();
    void submitStep(async () => {
      await callOperation('platform.subscription.create', {
        params: { tenant_uid: tenantUid },
        // FND-08: the SERVER decides how long a default trial is, from the Owner's policy row.
        // This used to send `trialEndsAt: null`, which is "no trial at all" -- so every customer
        // provisioned through the console started with none, whatever the policy said.
        body: { planCode, periodKind, trial: 'default' },
      });
      return { step: 3, tenantUid };
    });
  }

  function onAddSku(e: FormEvent, tenantUid: string): void {
    e.preventDefault();
    void submitStep(async () => {
      await callOperation('platform.subscription.sku.add', {
        params: { tenant_uid: tenantUid },
        body: { skuCode, quantity: Number.parseInt(quantity, 10) },
      });
      return { step: 4, tenantUid };
    });
  }

  function onInviteAdministrator(e: FormEvent, tenantUid: string): void {
    e.preventDefault();
    void submitStep(async () => {
      const invited = await callOperation('platform.tenant.activate', {
        params: { tenant_uid: tenantUid },
        body: { email: adminEmail, displayName: adminName },
      });
      return {
        step: 'done',
        tenantUid,
        invitationUid: invited.invitationUid,
        // Carried, not interpreted: the step that renders it decides what it means, and a field
        // dropped here is a field the screen cannot say anything about.
        ...(invited.delivery === undefined ? {} : { delivery: invited.delivery }),
        ...(invited.deliveryChannel === undefined
          ? {}
          : { deliveryChannel: invited.deliveryChannel }),
        ...(invited.expiresAt === undefined ? {} : { expiresAt: invited.expiresAt }),
        ...(invited.activated === undefined ? {} : { activated: invited.activated }),
      };
    });
  }

  /**
   * THE STEP THIS CUSTOMER IS UP TO, FROM THE SERVER'S OWN ANSWER.
   *
   *   no live subscription          -> step 2, open one
   *   a subscription, no modules    -> step 3, sell it something
   *   a subscription with modules   -> step 4, invite the first administrator
   *
   * THE ROLES ARE NOT A STEP HERE, and that is deliberate rather than an omission: the role copy
   * belongs to the server, and `ProvisioningService.#ensureBootstrapped` RE-ASSERTS it at step 4
   * for exactly this case -- *"a tenant reaching here without roles is a tenant whose creation
   * crashed between two port calls, and the only thing in the product that can fix it is this."*
   * `rafi-test-20261018` has zero roles, and step 4 is what gives it five. A separate "repair
   * roles" button would be a second answer to a question the server already answers.
   */
  const resumedProgress: Progress | null =
    resumeTenantUid === undefined || subscriptionRead.result.state !== 'loaded'
      ? null
      : subscriptionRead.result.data === null
        ? null
        : subscriptionRead.result.data.subscription === null
          ? { step: 2, tenantUid: resumeTenantUid }
          : subscriptionRead.result.data.subscription.licensedModules.length === 0
            ? { step: 3, tenantUid: resumeTenantUid }
            : { step: 4, tenantUid: resumeTenantUid };

  /*
   * What the operator did in this tab WINS over what the read said, because they have since
   * written to the server. The read is the starting point, not a controller.
   */
  const resolved: Progress = progress ?? resumedProgress ?? { step: 'resolving' };

  const resumedTenant =
    tenantsRead.result.state === 'loaded' && tenantsRead.result.data !== null
      ? (tenantsRead.result.data.tenants.find((row) => row.tenantUid === resumeTenantUid) ?? null)
      : null;

  const current = stepNumber(resolved);

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'הלקוחות', href: routes.platformTenants }}
    >
      {/* WHICH CUSTOMER IS BEING FINISHED, named before any form. A wizard that silently operated
          on a customer the operator could not see would be worse than no resume at all. */}
      {resumeTenantUid === undefined ? null : (
        /*
         * NOT a `<StateBlock>`: its four tones are `unavailable`, `error`, `empty` and `stale`, and
         * this is none of them. Finishing a customer that exists is the ordinary case of this
         * address, not a degraded one, and borrowing a failure tone to get a box drawn would make
         * the screen say something it does not mean.
         */
        <section className="form" aria-label="הלקוח שהקמתו מושלמת">
          <h2 className="form__title">
            {resumedTenant === null
              ? 'השלמת הקמה של לקוח קיים'
              : `השלמת הקמה: ${resumedTenant.legalName}`}
          </h2>
          {resumedTenant === null ? null : (
            <p className="field__hint" dir="ltr">
              {resumedTenant.slug}
            </p>
          )}
        </section>
      )}

      {resolved.step === 'resolving' ? (
        <ServerRead
          result={subscriptionRead.result}
          whatHe="את מצב ההקמה של הלקוח"
          skeletonRows={3}
          renderNotFound={() => (
            <StateBlock
              titleHe="הלקוח הזה אינו קיים"
              bodyHe="הכתובת מציינת לקוח שאינו קיים במערכת, ולכן אין הקמה להמשיך."
              tone="empty"
              nextStep={
                <a className="btn" href={routes.platformTenants}>
                  חזרה לרשימת הלקוחות
                </a>
              }
            />
          )}
        >
          {/* Never rendered: once the read is loaded the step is known and this block is gone. */}
          {() => null}
        </ServerRead>
      ) : null}

      {/* The four steps, always all four, with the current one named. A wizard that hides the
          remaining steps is a wizard whose length the operator discovers one screen at a time. */}
      <ol className="steps">
        {STEPS_HE.map((label, index) => (
          <li
            key={label}
            className="steps__item"
            data-state={index + 1 < current ? 'done' : index + 1 === current ? 'current' : 'pending'}
          >
            <span className="steps__index" dir="ltr">
              {index + 1}
            </span>
            <span className="steps__label">{label}</span>
          </li>
        ))}
      </ol>

      {failure === null ? null : failure.kind === 'denied' ? (
        <DenialNotice deny={failure.body} />
      ) : failure.kind === 'conflict' ? (
        conflictNotice(failure.reason, failure.requestUid)
      ) : failure.kind === 'error' ? (
        <ErrorState requestUid={failure.requestUid} />
      ) : (
        <StateBlock
          titleHe="הבקשה לא הגיעה לשרת"
          bodyHe="לא הצלחנו ליצור קשר עם השרת, ולכן השלב הזה לא בוצע."
          tone="error"
          // A write is never retried automatically, so the retry is the operator's explicit act.
          nextStep={<p>אפשר לשלוח את השלב שוב לאחר שהשרת יהיה זמין. שום דבר לא נשמר בינתיים.</p>}
        />
      )}

      {resolved.step === 1 ? (
        <form className="form" onSubmit={onCreateTenant}>
          <h2 className="form__title">שלב 1 — הקמת הלקוח</h2>
          <div className="form__group form__group--wide">
            <label className="field">
              <span className="field__label">השם המשפטי של הלקוח</span>
              <input
                className="field__input"
                value={legalName}
                onChange={(e) => setLegalName(e.currentTarget.value)}
                required
              />
            </label>

            <label className="field">
              <span className="field__label">מזהה ההקמה</span>
              <input
                className="field__input"
                dir="ltr"
                value={slug}
                onChange={(e) => setSlug(e.currentTarget.value)}
                required
              />
            </label>

            <fieldset className="field field--choice">
              <legend className="field__label">סוג הלקוח</legend>
              {TENANT_KINDS.map((k) => (
                <label key={k.code} className="choice">
                  <input
                    type="radio"
                    name="tenantKind"
                    value={k.code}
                    checked={kind === k.code}
                    onChange={() => setKind(k.code)}
                  />
                  <span>{k.labelHe}</span>
                </label>
              ))}
              <span className="field__hint">
                נרשם לצורכי דיווח בלבד.
              </span>
            </fieldset>

            <div className="actions-row">
              <button type="submit" className="btn btn--primary" disabled={pending}>
                {pending ? 'שולח…' : 'הקמת הלקוח'}
              </button>
            </div>
          </div>
        </form>
      ) : null}

      {resolved.step === 2 ? (
        <form className="form" onSubmit={(e) => onCreateSubscription(e, resolved.tenantUid)}>
          <h2 className="form__title">שלב 2 — פתיחת מנוי</h2>
          <div className="form__group form__group--wide">
            <label className="field">
              <span className="field__label">קוד התכנית</span>
              <input
                className="field__input"
                dir="ltr"
                value={planCode}
                onChange={(e) => setPlanCode(e.currentTarget.value)}
                required
              />
            </label>

            <fieldset className="field field--choice">
              <legend className="field__label">מחזור החיוב</legend>
              {PERIOD_KINDS.map((p) => (
                <label key={p.code} className="choice">
                  <input
                    type="radio"
                    name="periodKind"
                    value={p.code}
                    checked={periodKind === p.code}
                    onChange={() => setPeriodKind(p.code)}
                  />
                  <span>{p.labelHe}</span>
                </label>
              ))}
            </fieldset>

            <div className="actions-row">
              <button type="submit" className="btn btn--primary" disabled={pending}>
                {pending ? 'שולח…' : 'פתיחת מנוי'}
              </button>
            </div>
          </div>
        </form>
      ) : null}

      {resolved.step === 3 ? (
        <form className="form" onSubmit={(e) => onAddSku(e, resolved.tenantUid)}>
          <h2 className="form__title">שלב 3 — מודולים ומכסות שנרכשו</h2>
          <div className="form__group form__group--wide">
            <label className="field">
              <span className="field__label">קוד המוצר</span>
              <input
                className="field__input"
                dir="ltr"
                value={skuCode}
                onChange={(e) => setSkuCode(e.currentTarget.value)}
                required
              />
            </label>

            <label className="field">
              <span className="field__label">כמות שנרכשה</span>
              <input
                className="field__input"
                type="number"
                dir="ltr"
                min={1}
                value={quantity}
                onChange={(e) => setQuantity(e.currentTarget.value)}
                required
              />
              <span className="field__hint">
                הכמות היא המכסה שהלקוח רכש.
              </span>
            </label>

            <div className="actions-row">
              <button type="submit" className="btn btn--primary" disabled={pending}>
                {pending ? 'שולח…' : 'הוספה למנוי'}
              </button>
            </div>
          </div>
        </form>
      ) : null}

      {resolved.step === 4 ? (
        <form className="form" onSubmit={(e) => onInviteAdministrator(e, resolved.tenantUid)}>
          <h2 className="form__title">שלב 4 — הזמנת מנהל הארגון הראשון</h2>
          <div className="form__group form__group--wide">
            <label className="field">
              <span className="field__label">שם מנהל הארגון</span>
              <input
                className="field__input"
                value={adminName}
                onChange={(e) => setAdminName(e.currentTarget.value)}
                required
              />
            </label>

            <label className="field">
              <span className="field__label">דואר אלקטרוני</span>
              <input
                className="field__input"
                type="email"
                dir="ltr"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.currentTarget.value)}
                required
              />
            </label>

            <div className="actions-row">
              <button type="submit" className="btn btn--primary" disabled={pending}>
                {pending ? 'שולח…' : 'שליחת ההזמנה'}
              </button>
            </div>
          </div>
        </form>
      ) : null}

      {resolved.step === 'done' ? (
        <StateBlock
          /*
           * ── WHAT HAPPENED TO THE MESSAGE, IN THE TITLE. **Owner, 2026-10-23 §8.** ───────────
           *
           * This said "הלקוח הוקם וההזמנה נשלחה" on every path, including the one where the server
           * had just answered 202 / `mail_suppressed` — the invitation exists and nothing was sent
           * to anybody. The words now come from `deliveryTextFor`, the module the product already
           * uses for exactly this on the tenant side, so the platform console and the office say
           * the same thing about the same outcome instead of two different things.
           */
          titleHe={deliveryHeading(resolved)}
          bodyHe={deliveryBody(resolved)}
          /* `stale` — the one tone that means "this is true and something about it is not
             finished". `error` would be wrong: the invitation was written and is redeemable. */
          {...(resolved.delivery === undefined && !writtenToAFile(resolved.deliveryChannel)
            ? {}
            : { tone: 'stale' as const })}
          nextStep={
            <>
              <p>{deliveryNextStep(resolved)}</p>
              <div className="actions-row">
                <a className="btn btn--nav" href={routes.platformTenant(resolved.tenantUid)}>
                  פתיחת כרטיס הלקוח
                </a>
                <a className="btn" href={routes.platformTenants}>
                  חזרה לרשימת הלקוחות
                </a>
              </div>
            </>
          }
        />
      ) : null}
    </AppShell>
  );
}
